import { useEffect } from "react";
import { create } from "zustand";
import * as Sentry from "@sentry/react";
import { getAuthToken } from "@/services/supabase/auth/actions/getAuthToken";

const BFF_BASE_URL = import.meta.env.VITE_BFF_SERVICE_URL as string;

export type ConnectionStatus = "connecting" | "connected" | "disconnected";

interface ConnectionState {
  socket: WebSocket | null;
  status: ConnectionStatus;
  connect: () => void;
  close: () => void;
}

export const useConnectionStore = create<ConnectionState>((set, get) => ({
  socket: null,
  status: "disconnected",

  connect: () => {
    if (get().status === "connecting" || get().status === "connected") return;

    const wsUrl = `${BFF_BASE_URL.replace(/^http/, "ws")}/api/connect`;
    const socket = new WebSocket(wsUrl);

    set({ socket, status: "connecting" });

    let attempt = 0;
    const MAX_ATTEMPTS = 5;

    const scheduleReconnect = () => {
      if (attempt >= MAX_ATTEMPTS) {
        set({ status: "disconnected", socket: null });
        return;
      }
      const timeout = 2 ** attempt * 1000;
      attempt += 1;
      setTimeout(() => get().connect(), timeout);
    };

    socket.onopen = async () => {
      attempt = 0;
      set({ status: "connected" });
      console.info("WebSocket connected →", wsUrl);
      const token = await getAuthToken();
      socket.send(JSON.stringify({ type: "authenticate", token }));
    };

    socket.onerror = (ev) => {
      console.error("WebSocket error", ev);
      Sentry.captureException(ev);
    };

    socket.onclose = (ev) => {
      console.warn("WebSocket closed", ev);
      Sentry.captureException(ev.reason || ev);
      set({ status: "disconnected", socket: null });
      scheduleReconnect();
    };
  },

  close: () => {
    const s = get().socket;
    if (s) {
      s.onclose = null;
      s.close();
    }
    set({ socket: null, status: "disconnected" });
  },
}));

export const useConnection = () => {
  const { socket, status, connect, close } = useConnectionStore();

  useEffect(() => {
    connect();
  }, [connect]);

  return {
    connection: socket,
    status,
    isConnected: status === "connected",
    reconnect: connect,
    close,
  } as const;
};
